import { getApiBaseURL } from '../../apiCommon'
import { getWarehouseFacilityHeader } from '@/modules/warehouseModule/composables/useFacilityHeader'

const groupPrefix = createApiRoute(ApiModule.WAREHOUSE, ApiDataModel.INBOUND)
const inventoryPrefix = createApiRoute(ApiModule.WAREHOUSE, ApiDataModel.INVENTORY)
const labelingPrefix = createApiRoute(ApiModule.WAREHOUSE, ApiDataModel.LABELING)

const MAX_BUFFER_EVENTS_RETRIES = 10
const MAX_BUFFER_EVENTS_DELAY_MS = 30_000
const JITTER_FACTOR = 0.5

const noopDisconnect = (): void => undefined

export const apiInboundBuffer = {
	fetchBufferItems: async (
		status?: EInboundBufferItemStatus[],
		filters?: IInboundBufferItemsFilters,
		pagination?: IInboundBufferItemsPagination,
	): Promise<IInboundBufferItemsResponse> => {
		const { data, meta } = await apiCommon<IInboundBufferItem[]>(`${groupPrefix}/inbound-buffer-items`, {
			method: 'GET',
			headers: getWarehouseFacilityHeader(),
			...createQuery(pagination ?? undefined, {
				...(status?.length ? { status } : {}),
				supplier_id: filters?.supplierId ?? undefined,
				delivery_day: filters?.deliveryDay ?? undefined,
				search: filters?.search ?? undefined,
			}),
		})

		return {
			data: data.map(item => InboundBufferItem(item)),
			filterOptions: InboundBufferFilterOptions((meta as unknown as IInboundBufferFiltersMeta)?.filters),
			meta,
		}
	},

	fetchBufferItem: async (id: string): Promise<IApiResponseModel<IInboundBufferItemModel>> => {
		const { data } = await apiCommon<IInboundBufferItem>(`${groupPrefix}/inbound-buffer-items/${id}`, {
			method: 'GET',
			headers: getWarehouseFacilityHeader(),
		})

		return { data: InboundBufferItem(data) }
	},

	storeStorageUnits: async (id: string, body: IInboundBufferStoreStorageUnitsPayload): Promise<void> => {
		await apiCommon(`${inventoryPrefix}/inbound-buffer-items/${id}/storage-units`, {
			method: 'POST',
			body,
		})
	},

	confirmInboundBufferItems: async (inboundBufferItemIds: string[]): Promise<void> => {
		await apiCommon(`${groupPrefix}/inbound-buffer-items/confirm-storage`, {
			method: 'POST',
			body: { inbound_buffer_item_ids: inboundBufferItemIds },
		})
	},

	resetBufferItem: async (id: string): Promise<void> => {
		await apiCommon(`${groupPrefix}/inbound-buffer-items/${id}/reset`, {
			method: 'POST',
		})
	},

	requestStorageUnitLabelPrint: async (
		storageUnitId: string,
	): Promise<IApiResponseModel<IInboundBufferStorageUnitLabelPrintModel>> => {
		const response = await apiCommon<IInboundBufferStorageUnitLabelPrint>(
			`${labelingPrefix}/storage-units/${storageUnitId}/request-label`,
			{ method: 'POST' },
		)

		return { data: InboundBufferStorageUnitLabelPrint(response as unknown as IInboundBufferStorageUnitLabelPrint) }
	},

	receiveBufferEvents: (
		facilityId: string,
		options: IInboundBufferEventsOptions = {},
	): { disconnect: () => void } => {
		if (!facilityId?.trim()) {
			options.onError?.(new Error('Brak identyfikatora obiektu do połączenia ze strumieniem bufora.'))
			return { disconnect: noopDisconnect }
		}

		let eventSource: EventSource | null = null
		let reconnectTimer: ReturnType<typeof setTimeout> | null = null
		let retryCount = 0
		let disposed = false

		const clearReconnectTimer = () => {
			if (reconnectTimer !== null) {
				clearTimeout(reconnectTimer)
				reconnectTimer = null
			}
		}

		const closeConnection = () => {
			if (eventSource) {
				eventSource.close()
				eventSource = null
			}
		}

		const emitEvent = (type: string, data: string) => {
			options.onEvent?.({ type, data })
		}

		function scheduleReconnect() {
			if (disposed || retryCount >= MAX_BUFFER_EVENTS_RETRIES) {
				return false
			}

			const base = Math.min(1000 * 2 ** retryCount, MAX_BUFFER_EVENTS_DELAY_MS)
			const delay = base * (JITTER_FACTOR + Math.random() * JITTER_FACTOR)
			retryCount++

			reconnectTimer = setTimeout(() => {
				reconnectTimer = null
				if (!disposed) {
					// eslint-disable-next-line ts/no-use-before-define -- mutual recursion via deferred callback; both are hoisted function declarations
					connect()
				}
			}, delay)

			return true
		}

		function connect() {
			if (disposed) return

			clearReconnectTimer()
			closeConnection()

			const path = `${groupPrefix}/facilities/${facilityId}/buffer/events`
			const url = new URL(path, getApiBaseURL(path) as string)

			try {
				eventSource = new EventSource(url.toString(), {
					withCredentials: true,
				} as EventSourceInit)

				eventSource.onopen = () => {
					retryCount = 0
					options.onOpen?.()
				}

				eventSource.onmessage = (event: MessageEvent) => {
					emitEvent('message', event.data)
				}

				eventSource.addEventListener(INBOUND_BUFFER_SSE_EVENTS.BUFFER_ITEM_CREATED, (event: MessageEvent) => {
					emitEvent(INBOUND_BUFFER_SSE_EVENTS.BUFFER_ITEM_CREATED, event.data)
				})

				eventSource.addEventListener(
					INBOUND_BUFFER_SSE_EVENTS.BUFFER_ITEM_STORAGE_UNITS_CREATED,
					(event: MessageEvent) => {
						emitEvent(INBOUND_BUFFER_SSE_EVENTS.BUFFER_ITEM_STORAGE_UNITS_CREATED, event.data)
					},
				)

				eventSource.addEventListener(
					INBOUND_BUFFER_SSE_EVENTS.BUFFER_ITEM_RESET_TO_INITIAL_STATE,
					(event: MessageEvent) => {
						emitEvent(INBOUND_BUFFER_SSE_EVENTS.BUFFER_ITEM_RESET_TO_INITIAL_STATE, event.data)
					},
				)

				eventSource.addEventListener(INBOUND_BUFFER_SSE_EVENTS.LABEL_PREVIEW_READY, (event: MessageEvent) => {
					emitEvent(INBOUND_BUFFER_SSE_EVENTS.LABEL_PREVIEW_READY, event.data)
				})

				eventSource.addEventListener(
					INBOUND_BUFFER_SSE_EVENTS.LABEL_READY_FOR_DOWNLOAD,
					(event: MessageEvent) => {
						emitEvent(INBOUND_BUFFER_SSE_EVENTS.LABEL_READY_FOR_DOWNLOAD, event.data)
					},
				)

				eventSource.onerror = () => {
					if (disposed) return

					if (eventSource?.readyState === EventSource.CONNECTING) return

					closeConnection()

					if (!scheduleReconnect()) {
						const error = new Error('Błąd połączenia ze strumieniem bufora przyjęć.')
						options.onError?.(error)
					}
				}
			} catch (error) {
				const err =
					error instanceof Error ? error : new Error('Nie udało się utworzyć połączenia SSE bufora przyjęć.')
				options.onError?.(err)
			}
		}

		connect()

		return {
			disconnect: () => {
				disposed = true
				clearReconnectTimer()
				closeConnection()
			},
		}
	},
}
